import { IconButton, Typography } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { Alert } from "@mui/material";
import { BasicCellType, TableCellType } from "components/LazyTable";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useParams } from "react-router-dom";
import { ListRequestOfExamAttemptFilter } from "services/KnowfullyAdminClient";
import { dateToStandardFormat } from "utils/dateUtils";
import InvalidateExamAttempt from "./InvalidateExamAttempt";

export type ExamAttemptModel = {
  id: string;
  score: string;
  result: string;
  submittedDateTime: string;
  status: string;
};

const ExamAttemptsList = () => {
  const { formatMessage: t } = useIntl();
  const { userEvaluationService, examAttemptDetailsUrl } = useGlobalContext();

  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();

  const { data, isFetching } = useQuery(
    ["examAttempts", userSkuId],
    () =>
      userEvaluationService.getExamAttempts(
        ListRequestOfExamAttemptFilter.fromJS({
          pageSize: -1,
          sortField: "startDateTime",
          parameters: { userSkuId },
        })
      ),
    { keepPreviousData: true }
  );

  const rows = (data?.data ?? []).map((e) => {
    const graded = e.score !== null;
    const id =
      e.examPassed === true ? "pass" : e.examPassed === false ? "fail" : null;

    return {
      id: e.id,
      score: graded ? e.score + "%" : t({ id: "notYetGraded" }),
      result: graded && id ? t({ id: id }) : null,
      submittedDateTime: e.endDateTime
        ? dateToStandardFormat(e.endDateTime)
        : undefined,
      status: e.status,
    } as ExamAttemptModel;
  });

  const baseCell = {
    align: "left",
    width: 0,
    type: "text",
  } as BasicCellType;

  const cells: Array<TableCellType> = [
    {
      ...baseCell,
      accessor: "submittedDateTime",
      label: t({ id: "submittedDate" }),
    },
    {
      ...baseCell,
      accessor: "score",
      label: t({ id: "scoreLabel" }),
    },
    {
      ...baseCell,
      accessor: "result",
      label: t({ id: "result" }),
    },
    {
      ...baseCell,
      accessor: "status",
      label: t({ id: "statusLabel" }),
    },
    {
      accessor: "view",
      align: "center",
      width: 0,
      type: "node",
      View: ({ value }: { value: ExamAttemptModel }) => (
        <Link to={examAttemptDetailsUrl(userId, userSkuId, value.id)}>
          <IconButton size="small">
            <AssignmentIcon />
          </IconButton>
        </Link>
      ),
      label: t({ id: "viewAttempt" }),
      noSort: true,
    },
    {
      accessor: "",
      align: "right",
      width: 0,
      type: "node",
      label: t({ id: "actions" }),
      View: ({ value }: { value: ExamAttemptModel }) => (
        <InvalidateExamAttempt value={value} userId={userId!} />
      ),
      noSort: true,
    },
  ];

  return (
    <>
      {!isFetching && rows.length === 0 ? (
        <Alert severity="info">
          <Typography>
            <T id="noExamAttemptsYet" />
          </Typography>
        </Alert>
      ) : (
        <SortableTable
          {...{
            rows,
            loading: isFetching,
            dense: true,
            cells,
            allowPagination: true,
            title: t({ id: "examAttempts" }),
          }}
        />
      )}
    </>
  );
};

export default ExamAttemptsList;
